import React from 'react';

import CSSModules from 'react-css-modules';
import {useIntl} from 'react-intl';

import {LoadingIndicator, SearchInput, Text} from '@edume/magnificent';

import useFeatureFlag from '../../../hooks/useFeatureFlags';
import Dialog from '../Dialog';
import FilePicker from './FilePicker';
import {MediaGrid} from './MediaGrid';

import styles from './styles.module.scss';

interface MediaLibraryProps {
  type: 'image' | 'video';
  renderMedia: React.ReactNode[];
  showDeleteModal: boolean;
  isLibraryLoading: boolean;
  hasUploader: boolean;
  description?: React.ReactNode;
  startUpload: (files: File[]) => void;
  onCancel: () => void;
  onConfirmDelete: () => void;
  onSearch?: (searchValue: string) => void;
  searchQuery?: string;
}

const intlKeyMap = {
  image: {
    title: 'ImageLibrary.deleteModal.title',
    body: 'ImageLibrary.deleteModal.body',
    hintBox: 'ImageLibrary.deleteModal.hintBox',
    cancel: 'ImageLibrary.deleteModal.cancelButton',
    confirm: 'ImageLibrary.deleteModal.confirmButton',
  },
  video: {
    title: 'VideoLibrary.deleteModal.title',
    body: 'VideoLibrary.deleteModal.body',
    hintBox: 'VideoLibrary.deleteModal.hintBox',
    cancel: 'VideoLibrary.deleteModal.cancelButton',
    confirm: 'VideoLibrary.deleteModal.confirmButton',
  },
} as const;

const MediaLibraryComponent = ({
  type,
  renderMedia,
  showDeleteModal,
  isLibraryLoading,
  hasUploader,
  description,
  startUpload,
  onCancel,
  onConfirmDelete,
  onSearch,
  searchQuery,
}: MediaLibraryProps) => {
  const intl = useIntl();
  const isMediaLibrarySearchEnabled = useFeatureFlag('mediaLibrarySearch');

  const filePicker = (
    <div className={styles['library-filepicker-container']} key={0}>
      <FilePicker type={type} onFilePick={startUpload} isUploading={false} />
    </div>
  );
  const content =
    hasUploader && !searchQuery ? [filePicker, ...renderMedia] : renderMedia;

  const intlKeys = intlKeyMap[type];

  const renderContent = () => {
    if (isLibraryLoading) {
      return (
        <div styleName='inner-container'>
          <LoadingIndicator
            indicator='hexagons'
            containerStyle='centerAbsolute'
          />
        </div>
      );
    }

    if (!!searchQuery && !renderMedia.length) {
      return (
        <div styleName='inner-container'>
          <Text size='m'>
            {intl.formatMessage({id: 'MediaLibrary.noResults'})}
          </Text>
        </div>
      );
    }

    return <MediaGrid content={content} />;
  };

  return (
    <div styleName='library-container'>
      {showDeleteModal && (
        <Dialog
          titleKey={intlKeys.title}
          descriptionKey={intlKeys.body}
          hintBoxKey={intlKeys.hintBox}
          onCancel={onCancel}
          onConfirm={onConfirmDelete}
          cancelKey={intlKeys.cancel}
          confirmKey={intlKeys.confirm}
        />
      )}

      {!!description && (
        <div styleName='library-description'>{description}</div>
      )}

      {isMediaLibrarySearchEnabled && onSearch && (
        <SearchInput
          placeholder={intl.formatMessage({id: 'MediaLibrary.search'})}
          onSearchTermChange={(value) => {
            if (value === '') {
              onSearch('');
            }
          }}
          onFreeTextSearch={(value) => onSearch(value)}
          dropdownOptions={[]}
          noResultsHeader={intl.formatMessage({id: 'MediaLibrary.noResults'})}
          noResultsDescription={intl.formatMessage({
            id: 'MediaLibrary.noResults',
          })}
          dataAutomation='media-library-search-input'
        />
      )}
      <div styleName='media-grid-container'>{renderContent()}</div>
    </div>
  );
};

export const MediaLibrary = CSSModules(MediaLibraryComponent, styles);
